import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { keyframes } from '@mui/system';
import {
  Grid, Paper, Typography, Box, Card, CardContent,
  List, ListItem, ListItemIcon, ListItemText, Avatar,
  Chip, LinearProgress, Divider, Button, IconButton,
} from '@mui/material';
import {
  People as PeopleIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon,
  Book as BookIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  ArrowForward as ArrowIcon,
  EmojiEvents as TrophyIcon,
  HourglassEmpty as PendingIcon,
  Class as ClassIcon,
  Refresh as RefreshIcon,
  BarChart as ChartIcon,
  BeachAccess as LeaveIcon,
} from '@mui/icons-material';
import { studentAPI, teacherAPI, attendanceAPI, feeAPI, examinationAPI, homeworkAPI, noticeAPI, academicAPI } from '../services/api';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeSlideRight = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.15); }
  50%       { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
`;
const countUp = keyframes`
  from { opacity: 0; transform: scale(0.7); }
  to   { opacity: 1; transform: scale(1); }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
`;
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
// ─── Count-up hook ────────────────────────────────────────────────────────────
function useCountUp(target, duration = 1400) {
  const [count, setCount] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    let start = 0;
    const step = target / (duration / 16);
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer.current); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer.current);
  }, [target, duration]);
  return count;
}

// ─── SVG Donut Chart ──────────────────────────────────────────────────────────
function DonutChart({ value, max, color, bg = '#f0f0f0', size = 90, label }) {
  const r = 32, circ = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const [animated, setAnimated] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnimated(true), 400); return () => clearTimeout(t); }, []);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke={bg} strokeWidth="8" />
        <circle cx="36" cy="36" r={r} fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circ}
          strokeDashoffset={animated ? circ * (1 - pct / 100) : circ}
          strokeLinecap="round"
          transform="rotate(-90 36 36)"
          style={{ transition: 'stroke-dashoffset 1.3s cubic-bezier(0.4,0,0.2,1)' }}
        />
        <text x="36" y="40" textAnchor="middle" fontSize="13" fontWeight="800" fill={color}>
          {Math.round(pct)}%
        </text>
      </svg>
      {label && <Typography variant="caption" sx={{ mt: 0.5, fontWeight: 600, color: '#666', textAlign: 'center' }}>{label}</Typography>}
    </Box>
  );
}

// ─── Animated Progress Bar ────────────────────────────────────────────────────
function AnimatedBar({ value, color, animated }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (animated) { const t = setTimeout(() => setVal(value), 200); return () => clearTimeout(t); }
  }, [animated, value]);
  return (
    <LinearProgress
      variant="determinate"
      value={val}
      sx={{
        height: 10, borderRadius: 5, bgcolor: '#e0f2f1',
        '& .MuiLinearProgress-bar': {
          background: color,
          borderRadius: 5,
          transition: 'transform 1.2s cubic-bezier(0.4,0,0.2,1)',
        },
      }}
    />
  );
}

// ─── Stat Card with count-up ──────────────────────────────────────────────────
function AnimStatCard({ title, rawValue, icon: Icon, gradient, subtitle, onClick, prefix = '', suffix = '', delay = 0 }) {
  const count = useCountUp(typeof rawValue === 'number' ? rawValue : 0);
  const display = typeof rawValue === 'number'
    ? `${prefix}${count.toLocaleString('en-IN')}${suffix}`
    : rawValue;
  return (
    <Card
      onClick={onClick}
      sx={{
        background: gradient, color: 'white', borderRadius: 3,
        cursor: onClick ? 'pointer' : 'default',
        animation: `${fadeSlideUp} 0.55s ease-out ${delay}s both`,
        transition: 'transform 0.25s, box-shadow 0.25s',
        position: 'relative', overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-6px)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        } : {},
        '&::after': {
          content: '""', position: 'absolute',
          top: -30, right: -30, width: 100, height: 100,
          borderRadius: '50%', background: 'rgba(255,255,255,0.07)',
        },
        '&::before': {
          content: '""', position: 'absolute',
          bottom: -20, left: -10, width: 80, height: 80,
          borderRadius: '50%', background: 'rgba(255,255,255,0.05)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 0.5, fontWeight: 500, fontSize: 13 }}>
              {title}
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 800, mb: 0.5, letterSpacing: -1, animation: `${countUp} 0.5s ease-out ${delay + 0.3}s both` }}
            >
              {display}
            </Typography>
            {subtitle && <Typography variant="caption" sx={{ opacity: 0.78, fontSize: 11 }}>{subtitle}</Typography>}
          </Box>
          <Box sx={{
            p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            animation: `${pulseGlow} 3s ease-in-out infinite`,
          }}>
            <Icon sx={{ fontSize: 34 }} />
          </Box>
        </Box>
        {onClick && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.75 }}>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>View details</Typography>
            <ArrowIcon sx={{ fontSize: 12 }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Small Stat Tile ──────────────────────────────────────────────────────────
function MiniStatCard({ title, value, icon: Icon, color, bg, onClick, delay = 0 }) {
  const count = useCountUp(typeof value === 'number' ? value : 0);
  return (
    <Card onClick={onClick} sx={{
      cursor: onClick ? 'pointer' : 'default',
      borderRadius: 2.5, border: `1.5px solid ${color}25`,
      boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      animation: `${fadeSlideUp} 0.45s ease-out ${delay}s both`,
      transition: 'all 0.22s',
      '&:hover': onClick ? {
        transform: 'translateY(-4px)',
        boxShadow: `0 10px 28px ${color}30`,
        borderColor: color,
      } : {},
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: bg, width: 44, height: 44 }}>
            <Icon sx={{ color, fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
              {typeof value === 'number' ? count.toLocaleString('en-IN') : value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 11 }}>
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalStudents: 0, totalTeachers: 0, todayAttendance: 0,
    totalPresent: 0, totalAbsent: 0, monthlyFeeCollection: 0,
    totalExams: 0, totalHomework: 0, pendingLeaves: 0,
    totalClasses: 0, totalSubjects: 0, publishedNotices: 0,
    recentExams: [],
  });
  const [loaded, setLoaded] = useState(false);
  const [progressAnimated, setProgressAnimated] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { loadDashboardData(); }, []);
  useEffect(() => {
    if (loaded) { const t = setTimeout(() => setProgressAnimated(true), 600); return () => clearTimeout(t); }
  }, [loaded]);

  const loadDashboardData = async () => {
    setRefreshing(true);
    try {
      const [students, teachers, attendance, payments, exams, homework, leaves, notices, classes, subjects] = await Promise.all([
        studentAPI.getAll(),
        teacherAPI.getAll(),
        attendanceAPI.getTodayStats(),
        feeAPI.getAllPayments().catch(() => ({ data: [] })),
        examinationAPI.getAll().catch(() => ({ data: [] })),
        homeworkAPI.getAll().catch(() => ({ data: [] })),
        attendanceAPI.getLeaves().catch(() => ({ data: [] })),
        noticeAPI.getAll().catch(() => ({ data: [] })),
        academicAPI.getClasses().catch(() => ({ data: [] })),
        academicAPI.getSubjects().catch(() => ({ data: [] })),
      ]);
      const pct = attendance.data.total > 0
        ? Math.round((attendance.data.present / attendance.data.total) * 100) : 0;
      const totalCollection = payments.data.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
      const pendingLeaves = leaves.data.filter(l =>
        (l.status || '').toUpperCase() === 'PENDING'
      ).length;
      const publishedNotices = notices.data.filter(n => n.isPublished || n.published).length;
      setStats({
        totalStudents: students.data.length,
        totalTeachers: teachers.data.length,
        todayAttendance: pct,
        totalPresent: attendance.data.present || 0,
        totalAbsent: attendance.data.absent || 0,
        monthlyFeeCollection: totalCollection,
        totalExams: exams.data.length,
        totalHomework: homework.data.length,
        pendingLeaves,
        totalClasses: classes.data.length,
        totalSubjects: subjects.data.length,
        publishedNotices,
        recentExams: exams.data.slice(-4).reverse(),
      });
    } catch (e) { console.error(e); }
    setLoaded(true);
    setRefreshing(false);
  };

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const quickActions = [
    { title: 'Mark Attendance', icon: CheckCircleIcon, path: '/attendance', color: '#ff9800', bg: '#fff3e0' },
    { title: 'Record Payment', icon: MoneyIcon, path: '/fees', color: '#4caf50', bg: '#e8f5e9' },
    { title: 'Add Student', icon: PeopleIcon, path: '/students', color: '#2196f3', bg: '#e0f2f1' },
    { title: 'Enter Marks', icon: AssignmentIcon, path: '/examinations', color: '#9c27b0', bg: '#f3e5f5' },
    { title: 'Add Assignment', icon: BookIcon, path: '/homework', color: '#f44336', bg: '#ffebee' },
    { title: 'Post Notice', icon: NotificationsIcon, path: '/notices', color: '#00695c', bg: '#e0f2f1' },
  ];

  const menuCards = [
    { title: 'Students', subtitle: 'Manage records', icon: PeopleIcon, path: '/students', gradient: 'linear-gradient(135deg,#00796b,#26a69a)' },
    { title: 'Faculty', subtitle: 'Teaching staff', icon: PersonIcon, path: '/teachers', gradient: 'linear-gradient(135deg,#00796b,#43a047)' },
    { title: 'Attendance', subtitle: 'Daily tracking', icon: CheckCircleIcon, path: '/attendance', gradient: 'linear-gradient(135deg,#e65100,#fb8c00)' },
    { title: 'Fees', subtitle: 'Collection & reports', icon: MoneyIcon, path: '/fees', gradient: 'linear-gradient(135deg,#6a1b9a,#ab47bc)' },
    { title: 'Examinations', subtitle: 'Exams & results', icon: AssignmentIcon, path: '/examinations', gradient: 'linear-gradient(135deg,#b71c1c,#e53935)' },
    { title: 'Assignments', subtitle: 'Assignments', icon: BookIcon, path: '/homework', gradient: 'linear-gradient(135deg,#0277bd,#039be5)' },
    { title: 'Notices', subtitle: 'Announcements', icon: NotificationsIcon, path: '/notices', gradient: 'linear-gradient(135deg,#37474f,#546e7a)' },
    { title: 'Reports', subtitle: 'Analytics & PDF', icon: TrendingUpIcon, path: '/reports', gradient: 'linear-gradient(135deg,#00695c,#00897b)' },
  ];

  const attendancePct = stats.todayAttendance;

  return (
    <Box>
      {/* ── College Banner ─────────────────────────────────────────────── */}
      <Paper
        sx={{
          mb: 3, borderRadius: 3, overflow: 'hidden',
          background: 'linear-gradient(135deg, #004d40 0%, #00796b 50%, #26a69a 100%)',
          animation: `${fadeSlideRight} 0.5s ease-out`,
          position: 'relative',
        }}
      >
        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', top: -20, right: 80, width: 120, height: 120,
          borderRadius: '50%', border: '2px solid rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', top: 10, right: 60, width: 60, height: 60,
          borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -10, left: '40%', width: 80, height: 80,
          borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2,
              animation: `${float} 3s ease-in-out infinite`,
            }}>
              <SchoolIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2 }}>
                College of Nursing
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5, display: 'block' }}>
                Nursing College Management System — Admin Dashboard
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Chip label={`${stats.totalStudents} Students`} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 10, height: 20 }} />
                <Chip label={`${stats.totalTeachers} Faculty`} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 10, height: 20 }} />
                <Chip label={`${stats.totalClasses} Programs`} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.10)', color: '#fff', fontSize: 10, height: 20 }} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.8 }}>
            <Chip
              icon={<CalendarIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
              label={today}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: 12, backdropFilter: 'blur(8px)' }}
            />
            {stats.todayAttendance < 75 && (
              <Chip
                icon={<WarningIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label="Low Attendance Today"
                sx={{ bgcolor: 'rgba(239,83,80,0.7)', color: '#fff', fontWeight: 600, fontSize: 11 }}
              />
            )}
            {stats.pendingLeaves > 0 && (
              <Chip
                icon={<PendingIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label={`${stats.pendingLeaves} Leave Request${stats.pendingLeaves > 1 ? 's' : ''} Pending`}
                sx={{ bgcolor: 'rgba(255,152,0,0.7)', color: '#fff', fontWeight: 600, fontSize: 11 }}
              />
            )}
            <IconButton onClick={loadDashboardData} size="small"
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? `${rotate} 1s linear infinite` : 'none' }} />
            </IconButton>
          </Box>
        </Box>
        {(!loaded || refreshing) && <LinearProgress sx={{ height: 3 }} />}
      </Paper>

      {/* ── Primary Stat Cards ────────────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <AnimStatCard
            title="Total Students" rawValue={stats.totalStudents} icon={PeopleIcon}
            gradient="linear-gradient(135deg, #00897b 0%, #004d40 100%)"
            subtitle="Enrolled students" delay={0}
            onClick={() => navigate('/students')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnimStatCard
            title="Total Faculty" rawValue={stats.totalTeachers} icon={PersonIcon}
            gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
            subtitle="Teaching staff" delay={0.1}
            onClick={() => navigate('/teachers')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnimStatCard
            title="Today's Attendance" rawValue={stats.todayAttendance} icon={CheckCircleIcon}
            gradient={stats.todayAttendance >= 75
              ? 'linear-gradient(135deg, #ffa726 0%, #ef6c00 100%)'
              : 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'}
            subtitle={`${stats.totalPresent} present · ${stats.totalAbsent} absent`}
            suffix="%" delay={0.2}
            onClick={() => navigate('/attendance')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnimStatCard
            title="Fee Collection" rawValue={Math.round(stats.monthlyFeeCollection)} icon={MoneyIcon}
            gradient="linear-gradient(135deg, #0288d1 0%, #26c6da 100%)"
            subtitle="Total collected (all time)" delay={0.3} prefix="₹"
            onClick={() => navigate('/fees')}
          />
        </Grid>
      </Grid>

      {/* ── Secondary Stat Tiles ──────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStatCard title="Exams" value={stats.totalExams} icon={AssignmentIcon}
            color="#00796b" bg="#e0f2f1" delay={0.35} onClick={() => navigate('/examinations')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStatCard title="Assignments" value={stats.totalHomework} icon={BookIcon}
            color="#0277bd" bg="#e1f5fe" delay={0.4} onClick={() => navigate('/homework')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStatCard title="Pending Leaves" value={stats.pendingLeaves} icon={LeaveIcon}
            color={stats.pendingLeaves > 0 ? '#e65100' : '#558b2f'}
            bg={stats.pendingLeaves > 0 ? '#fff3e0' : '#f1f8e9'}
            delay={0.45} onClick={() => navigate('/attendance')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStatCard title="Programs" value={stats.totalClasses} icon={ClassIcon}
            color="#6a1b9a" bg="#f3e5f5" delay={0.5} onClick={() => navigate('/configuration')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStatCard title="Subjects" value={stats.totalSubjects} icon={SchoolIcon}
            color="#00695c" bg="#e0f2f1" delay={0.55} onClick={() => navigate('/configuration')} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MiniStatCard title="Notices" value={stats.publishedNotices} icon={NotificationsIcon}
            color="#c62828" bg="#ffebee" delay={0.6} onClick={() => navigate('/notices')} />
        </Grid>
      </Grid>

      {/* ── Middle Row ────────────────────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', animation: `${fadeSlideUp} 0.55s ease-out 0.35s both` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <Avatar sx={{ bgcolor: '#e0f2f1', mr: 1.5, width: 36, height: 36 }}>
                <TrendingUpIcon sx={{ color: '#00796b', fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d40' }}>Quick Actions</Typography>
            </Box>
            <Grid container spacing={1.5}>
              {quickActions.map((action, i) => (
                <Grid item xs={4} key={action.title}>
                  <Card
                    onClick={() => navigate(action.path)}
                    sx={{
                      p: 1.5, cursor: 'pointer', textAlign: 'center',
                      border: `1.5px solid ${action.color}30`,
                      boxShadow: 'none', borderRadius: 2.5,
                      animation: `${fadeSlideUp} 0.4s ease-out ${0.4 + i * 0.07}s both`,
                      transition: 'all 0.22s',
                      '&:hover': {
                        borderColor: action.color,
                        transform: 'translateY(-3px)',
                        boxShadow: `0 6px 20px ${action.color}30`,
                        bgcolor: action.bg,
                      },
                    }}
                  >
                    <Avatar sx={{ bgcolor: action.bg, color: action.color, mx: 'auto', mb: 0.8, width: 38, height: 38 }}>
                      <action.icon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 10, lineHeight: 1.3, display: 'block' }}>
                      {action.title}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Attendance + Fee Donuts */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', animation: `${fadeSlideUp} 0.55s ease-out 0.45s both` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: '#e8f5e9', mr: 1.5, width: 36, height: 36 }}>
                <StarIcon sx={{ color: '#00796b', fontSize: 20 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d40' }}>Today at a Glance</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', py: 1 }}>
              <DonutChart value={stats.totalPresent} max={stats.totalPresent + stats.totalAbsent} color="#4caf50" bg="#e8f5e9" label="Attendance" />
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <DonutChart value={Math.min(stats.monthlyFeeCollection, 500000)} max={500000} color="#00796b" bg="#e0f2f1" label="Fee Target" />
            </Box>
            <Divider sx={{ my: 2 }} />
            <List disablePadding dense>
              <ListItem sx={{ px: 0, py: 0.8 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Avatar sx={{ bgcolor: '#e8f5e9', width: 30, height: 30 }}>
                    <PeopleIcon sx={{ color: '#00796b', fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary="Present" secondary={`${stats.totalPresent} students`} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
                <Chip label={`${stats.todayAttendance}%`} size="small" color={stats.todayAttendance >= 75 ? 'success' : 'warning'} />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.8 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Avatar sx={{ bgcolor: '#ffebee', width: 30, height: 30 }}>
                    <PersonIcon sx={{ color: '#c62828', fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary="Absent" secondary="Requires attention" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
                <Chip label={stats.totalAbsent} size="small" color="error" variant="outlined" />
              </ListItem>
              <ListItem sx={{ px: 0, py: 0.8 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Avatar sx={{ bgcolor: '#e0f2f1', width: 30, height: 30 }}>
                    <MoneyIcon sx={{ color: '#00796b', fontSize: 16 }} />
                  </Avatar>
                </ListItemIcon>
                <ListItemText primary="Fee Collected" secondary="All time total" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
                <Chip label={`₹${Math.round(stats.monthlyFeeCollection).toLocaleString('en-IN')}`} size="small" color="primary" variant="outlined" />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Exam & Marks Overview */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', animation: `${fadeSlideUp} 0.55s ease-out 0.5s both` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#e0f2f1', mr: 1.5, width: 36, height: 36 }}>
                  <AssignmentIcon sx={{ color: '#00796b', fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d40' }}>Exam & Marks</Typography>
              </Box>
              <Button size="small" onClick={() => navigate('/examinations')}
                sx={{ borderRadius: 2, fontSize: 10, fontWeight: 700, color: '#00796b',
                  border: '1px solid #c5cae9', minWidth: 'auto', px: 1.5, py: 0.5 }}>
                View All
              </Button>
            </Box>

            {/* Mini stat boxes */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e0f2f1', textAlign: 'center',
                  animation: `${fadeSlideUp} 0.4s ease-out 0.6s both` }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#00796b' }}>{stats.totalExams}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10 }}>Exams</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e8f5e9', textAlign: 'center',
                  animation: `${fadeSlideUp} 0.4s ease-out 0.68s both` }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#00796b' }}>{stats.totalHomework}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10 }}>Assignments</Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ p: 1.5, borderRadius: 2,
                  bgcolor: stats.pendingLeaves > 0 ? '#fff3e0' : '#f3e5f5', textAlign: 'center',
                  animation: `${fadeSlideUp} 0.4s ease-out 0.76s both` }}>
                  <Typography variant="h5" sx={{ fontWeight: 800,
                    color: stats.pendingLeaves > 0 ? '#e65100' : '#6a1b9a' }}>
                    {stats.pendingLeaves}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: 10 }}>Leaves</Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 1.5 }} />

            <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', mb: 1, display: 'block',
              textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Recent Exams
            </Typography>

            {stats.recentExams.length === 0 ? (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 36, color: '#c5cae9', mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>No exams created yet</Typography>
                <Button size="small" variant="contained" onClick={() => navigate('/examinations')}
                  sx={{ mt: 1.5, background: 'linear-gradient(135deg,#00796b,#26a69a)', borderRadius: 2, fontSize: 11 }}>
                  Create First Exam
                </Button>
              </Box>
            ) : (
              <List disablePadding dense>
                {stats.recentExams.map((exam, i) => (
                  <ListItem key={exam.id} sx={{ px: 0, py: 0.5,
                    animation: `${fadeSlideUp} 0.4s ease-out ${0.65 + i * 0.08}s both` }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Avatar sx={{
                        bgcolor: ['#e0f2f1','#e8f5e9','#fff3e0','#fce4ec'][i % 4],
                        width: 26, height: 26,
                      }}>
                        <AssignmentIcon sx={{
                          color: ['#00796b','#00796b','#e65100','#c62828'][i % 4],
                          fontSize: 13,
                        }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={exam.name || exam.title || 'Exam'}
                      secondary={exam.examType || exam.description || 'General'}
                      primaryTypographyProps={{ fontSize: 12, fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: 10 }}
                    />
                    <Chip
                      label={exam.status === 'COMPLETED' ? 'Done' : exam.status === 'ACTIVE' ? 'Active' : 'Scheduled'}
                      size="small"
                      sx={{
                        fontSize: 9, height: 18, fontWeight: 700,
                        bgcolor: exam.status === 'COMPLETED' ? '#e8f5e9' : exam.status === 'ACTIVE' ? '#e0f2f1' : '#e0f2f1',
                        color: exam.status === 'COMPLETED' ? '#00796b' : exam.status === 'ACTIVE' ? '#00796b' : '#00796b',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ── Performance Overview Row ──────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>

        {/* College Performance Metrics */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, animation: `${fadeSlideUp} 0.55s ease-out 0.6s both` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#fce4ec', mr: 1.5, width: 36, height: 36 }}>
                  <ChartIcon sx={{ color: '#c62828', fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d40' }}>College Performance Overview</Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                {[
                  { label: 'Student Enrollment', value: stats.totalStudents, max: 500, color: 'linear-gradient(90deg,#00897b,#004d40)' },
                  { label: 'Teaching Staff', value: stats.totalTeachers, max: 50, color: 'linear-gradient(90deg,#11998e,#38ef7d)' },
                  { label: 'Today\'s Attendance', value: stats.todayAttendance, max: 100, color: attendancePct >= 75 ? 'linear-gradient(90deg,#4caf50,#8bc34a)' : 'linear-gradient(90deg,#ff9800,#ffc107)', suffix: '%' },
                ].map((item, i) => (
                  <Box key={item.label} sx={{ mb: 2.5, animation: `${fadeSlideUp} 0.4s ease-out ${0.65 + i * 0.1}s both` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>{item.label}</Typography>
                      <Typography variant="body2" fontWeight={700} color="#004d40">
                        {item.value}{item.suffix || ''} / {item.max}{item.suffix || ''}
                      </Typography>
                    </Box>
                    <AnimatedBar value={(item.value / item.max) * 100} color={item.color} animated={progressAnimated} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {Math.round((item.value / item.max) * 100)}% of target
                    </Typography>
                  </Box>
                ))}
              </Grid>
              <Grid item xs={12} sm={6}>
                {[
                  { label: 'Exams Conducted', value: stats.totalExams, max: Math.max(stats.totalExams, 20), color: 'linear-gradient(90deg,#00796b,#26a69a)' },
                  { label: 'Assignments Given', value: stats.totalHomework, max: Math.max(stats.totalHomework, 50), color: 'linear-gradient(90deg,#0277bd,#039be5)' },
                  { label: 'Fee Collection (₹5L target)', value: Math.min(stats.monthlyFeeCollection, 500000), max: 500000, color: 'linear-gradient(90deg,#00695c,#00897b)' },
                ].map((item, i) => (
                  <Box key={item.label} sx={{ mb: 2.5, animation: `${fadeSlideUp} 0.4s ease-out ${0.7 + i * 0.1}s both` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>{item.label}</Typography>
                      <Typography variant="body2" fontWeight={700} color="#004d40">
                        {item.label.includes('₹') ? `₹${Math.round(item.value).toLocaleString('en-IN')}` : item.value}
                      </Typography>
                    </Box>
                    <AnimatedBar value={item.max > 0 ? (item.value / item.max) * 100 : 0} color={item.color} animated={progressAnimated} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {item.max > 0 ? Math.round((item.value / item.max) * 100) : 0}% achieved
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Marks Grade Guide */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%', animation: `${fadeSlideUp} 0.55s ease-out 0.65s both` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
              <Avatar sx={{ bgcolor: '#fff3e0', mr: 1.5, width: 36, height: 36 }}>
                <TrophyIcon sx={{ color: '#e65100', fontSize: 20, animation: `${float} 2.5s ease-in-out infinite` }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d40' }}>Grade Scale</Typography>
            </Box>

            {[
              { grade: 'A+', range: '91–100', color: '#00796b', bg: '#e8f5e9', label: 'Outstanding' },
              { grade: 'A',  range: '81–90',  color: '#00796b', bg: '#e0f2f1', label: 'Excellent' },
              { grade: 'B',  range: '71–80',  color: '#6a1b9a', bg: '#f3e5f5', label: 'Very Good' },
              { grade: 'C',  range: '61–70',  color: '#e65100', bg: '#fff3e0', label: 'Good' },
              { grade: 'D',  range: '51–60',  color: '#f57f17', bg: '#fffde7', label: 'Satisfactory' },
              { grade: 'F',  range: 'Below 50', color: '#c62828', bg: '#ffebee', label: 'Needs Improvement' },
            ].map((g, i) => (
              <Box key={g.grade} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2,
                animation: `${fadeSlideUp} 0.35s ease-out ${0.7 + i * 0.07}s both`,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.5, bgcolor: g.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, color: g.color }}>{g.grade}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, lineHeight: 1 }}>{g.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{g.range}%</Typography>
                </Box>
                <Chip label={g.range} size="small"
                  sx={{ bgcolor: g.bg, color: g.color, fontWeight: 700, fontSize: 10, height: 20 }} />
              </Box>
            ))}

            <Divider sx={{ my: 1.5 }} />
            <Button fullWidth variant="outlined" onClick={() => navigate('/examinations')}
              sx={{ borderRadius: 2, fontWeight: 700, fontSize: 12, borderColor: '#00796b', color: '#00796b',
                '&:hover': { bgcolor: '#e0f2f1' } }}>
              Enter Exam Marks
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* ── Module Cards ──────────────────────────────────────────────── */}
      <Box sx={{ animation: `${fadeSlideUp} 0.55s ease-out 0.7s both` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, background: 'linear-gradient(#004d40,#00796b)' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#004d40' }}>Modules</Typography>
        </Box>
        <Grid container spacing={2}>
          {menuCards.map((card, i) => (
            <Grid item xs={6} sm={3} md={3} key={card.title}>
              <Card
                onClick={() => navigate(card.path)}
                sx={{
                  cursor: 'pointer', textAlign: 'center', p: 2.5,
                  borderRadius: 3, border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  animation: `${fadeSlideUp} 0.4s ease-out ${0.72 + i * 0.06}s both`,
                  transition: 'all 0.25s',
                  position: 'relative', overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                    borderColor: 'transparent',
                  },
                  '&:hover .card-bg': {
                    opacity: 0.08,
                  },
                }}
              >
                <Box className="card-bg" sx={{
                  position: 'absolute', inset: 0, background: card.gradient,
                  opacity: 0, transition: 'opacity 0.25s',
                }} />
                <Avatar
                  sx={{
                    background: card.gradient, mx: 'auto', mb: 1.5,
                    width: 52, height: 52, boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                    transition: 'transform 0.25s',
                    '.MuiCard-root:hover &': { transform: 'scale(1.12)' },
                  }}
                >
                  <card.icon sx={{ fontSize: 26 }} />
                </Avatar>
                <Typography variant="body1" sx={{ fontWeight: 700, mb: 0.3, fontSize: 13, position: 'relative' }}>{card.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, position: 'relative' }}>{card.subtitle}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
